import { useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { TokenUsageCopy, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import BreakdownList from './BreakdownList'
import PeopleBreakdown from './PeopleBreakdown'

interface Props {
  workerId: string
  report: TokenUsageReport
  copy: TokenUsageCopy
  canManage: boolean
}

export default function UsageBreakdowns({ workerId, report, copy, canManage }: Props) {
  const reduceMotion = useReducedMotion()
  const [activeTab, setActiveTab] = useState<'channels' | 'routines' | 'work-types' | 'people'>('channels')
  const [direction, setDirection] = useState(1)
  const activeIndex = useRef(0)
  const tabs = [
    { value: 'channels' as const, label: copy.tabs.channels },
    { value: 'people' as const, label: copy.tabs.people },
    { value: 'routines' as const, label: copy.tabs.routines },
    { value: 'work-types' as const, label: copy.tabs.work_types },
  ]

  const changeTab = (value: string) => {
    const nextTab = value as typeof activeTab
    const nextIndex = tabs.findIndex((tab) => tab.value === nextTab)
    setDirection(nextIndex >= activeIndex.current ? 1 : -1)
    activeIndex.current = nextIndex
    setActiveTab(nextTab)
  }

  const panel = activeTab === 'channels'
    ? <BreakdownList items={report.channels} copy={copy} showChannelIcons />
    : activeTab === 'routines'
    ? <BreakdownList items={report.routines} copy={copy} />
    : activeTab === 'work-types'
      ? <BreakdownList items={report.work_types} copy={copy} />
      : <PeopleBreakdown workerId={workerId} items={report.people} canonicalPeople={report.canonical_people} copy={copy} canManage={canManage} />

  return (
    <div className="px-4 pb-5 pt-2 md:px-5 md:pb-5">
      <div className="mb-4">
        <h2 className="text-[14px] font-semibold text-foreground">{copy.breakdown_title}</h2>
        <p className="mt-1 text-[12.5px] text-muted-foreground">{copy.breakdown_description}</p>
      </div>
      <Tabs value={activeTab} onValueChange={changeTab}>
        <TabsList className="max-w-full justify-start overflow-x-auto">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="relative isolate flex-none overflow-hidden px-1.5 text-[11.5px] data-[state=active]:bg-transparent data-[state=active]:shadow-none sm:px-2 sm:text-sm dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-transparent"
            >
              {activeTab === tab.value && (
                <motion.span
                  layoutId="token-usage-breakdown-tab"
                  aria-hidden="true"
                  className="absolute inset-0 -z-10 rounded-md border border-border bg-card shadow-xs"
                  transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 38, mass: 0.55 }}
                />
              )}
              <span className="relative">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <TabsContent key={activeTab} value={activeTab} forceMount asChild>
            <motion.div
              custom={direction}
              initial={reduceMotion ? false : { opacity: 0, x: direction * 7 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, x: direction * -5 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {panel}
            </motion.div>
          </TabsContent>
        </AnimatePresence>
      </Tabs>
    </div>
  )
}
